// pipe: 4A.6 ERP module config — registers ErpService at the ROOT framework scope (session caches).
export type {
  AccountRow,
  CertificateIssueResult,
  CrmProfitabilityReport,
  CrmQbrReport,
  CrmSlaReport,
  DirectoryUser,
  EntitySchema,
  FacilityCertification,
  FacilityDetails,
  FacilityMember,
  FacilityRow,
  FieldSchema,
  LifecycleSchema,
  OpsDashboardReport,
  OrderPnl,
  OrderSlaClocks,
  OrderSlaReport,
  ProvisioningInfo,
  RecordPage,
  RecordRow,
  StageMetric,
  StationKey,
  WipeEvidenceResult,
} from './services/erp';
export { ErpService } from './services/erp';
export {
  useErpProvisioning,
  useErpProvisioningStatus,
} from './use-erp-provisioning';
// Work (4H) + QMS (4F) DTOs — grouped per family (append-style, mirrors the erp.ts blocks).
export type {
  BoardView,
  BoardViewBucket,
  BoardViewDay,
  OrgChart,
  OrgChartEdge,
  OrgChartNode,
  QmsDashboardReport,
  TrainingCourseCol,
  TrainingMatrixReport,
  TrainingMatrixRow,
  WorkAggregate,
  WorkAssigneeAggregate,
  WorkBoardAggregate,
  WorkDashboardReport,
} from './services/erp';
export type {
  DirectorCompany,
  DirectorFacility,
  DirectorOrganization,
  DirectorSetup,
} from './services/erp';
// Director Tasks wire types (contract: docs/director-brief-contract.md).
export type {
  DirectorAllowedTaskAction,
  DirectorAttachment,
  DirectorBoundedList,
  DirectorBrief,
  DirectorBriefLine,
  DirectorBriefPayload,
  DirectorBriefTone,
  DirectorBulkUpdateInput,
  DirectorCadence,
  DirectorChecklistInput,
  DirectorChecklistItem,
  DirectorClearAction,
  DirectorClearedAction,
  DirectorClearedItem,
  DirectorClearInboxInput,
  DirectorCreateTaskInput,
  DirectorDelivery,
  DirectorDeliveryKind,
  DirectorDeliveryReason,
  DirectorEodPromptPayload,
  DirectorEventKind,
  DirectorInboxItem,
  DirectorInboxKind,
  DirectorMe,
  DirectorMyDay,
  DirectorMyDayTask,
  DirectorOutcome,
  DirectorParsed,
  DirectorPersonFlag,
  DirectorPersonTaskFilter,
  DirectorPersonTasks,
  DirectorReach,
  DirectorRecurrence,
  DirectorRecurrenceStopInput,
  DirectorRecurrenceTemplate,
  DirectorRecurrenceUpdateInput,
  DirectorRepeatPattern,
  DirectorScope,
  DirectorStaffPerson,
  DirectorTask,
  DirectorTaskActionInput,
  DirectorTaskDetail,
  DirectorTaskEvent,
  DirectorTaskPage,
  DirectorTaskStatus,
  DirectorTaskWrite,
  DirectorTeamPerson,
} from './director-tasks-types';
export {
  DIRECTOR_ATTACHMENT_MAX_BYTES,
  DIRECTOR_ATTACHMENTS_PER_TASK,
  DIRECTOR_ATTACHMENTS_PER_WRITE,
  DIRECTOR_CADENCES,
  DIRECTOR_CLEAR_ACTIONS,
  DIRECTOR_DELIVERY_KINDS,
  DIRECTOR_DELIVERY_REASONS,
  DIRECTOR_EVENT_KINDS,
  DIRECTOR_INBOX_KINDS,
  DIRECTOR_OUTCOMES,
  DIRECTOR_PERSON_FLAGS,
  DIRECTOR_TASK_ACTIONS,
  DIRECTOR_TASK_STATUSES,
} from './director-tasks-types';
// Frontend wave FB (refining / comms / agreements+inbound) — appended block.
export type {
  AuditTrailEntry,
  CommsPreviewRequest,
  CommsPreviewResult,
  CommsRow,
  CommsSendRequest,
  CommsTestEventRequest,
  LineageBar,
  LotLineageReport,
  LotMassBalanceReport,
} from './services/erp';
export type { PortalInviteCreated, PortalInviteRow } from './services/erp';
// Self-service My profile wire types (docs/my-profile.md).
export type { MyProfile, MyProfileChange } from './my-profile-types';
export { ErpApiError, isAuthError } from './services/erp';
// Frontend wave F5 (sales / listings / org-chart canvas) — appended block.
export type {
  AvailableStock,
  CampaignRoiReport,
  DiagramMintResult,
  LeadConvertReceipt,
  OutreachReceipt,
  OutreachRequest,
  PipelineReport,
  PipelineValue,
  QuoteConvertReceipt,
  StatusCount,
} from './services/erp';
// Reports (ROMS+Monday P0.1) — appended block.
export type {
  AccountLookupRow,
  AccountRef,
  AccountRollup,
  ReportCatalogEntry,
  ReportColumn,
  ReportColumnKind,
  ReportExportRequest,
  ReportHeaderStyle,
  ReportOverview,
  ReportPage,
  ReportParamSpec,
  ReportParamType,
  ReportPreamble,
  ReportPreambleGroup,
  ReportRowDocument,
  ReportRowExport,
  ReportRowStyle,
  ReportRunResult,
  ReportSheetBatch,
  ReportSheetBatchRequest,
  ReportSheetExpect,
  ReportSheetRef,
  ReportSheetStyle,
} from './services/erp';
// Floor stations (docs/floor-stations-contract.md) — appended block.
export type { DraftLine, InvoiceDraft } from './services/erp';
// Receiving→eval wave (slice B) — the receiving read-model DTOs.
export type {
  OrderReceivingReport,
  ReceivingPallet,
  ReceivingTotals,
} from './services/erp';
export { STATION_QUEUE_LIMIT } from './services/erp';
export { ACCOUNT_LOOKUP_LIMIT } from './services/erp';
// P3.1 (track D) — Bids intake DTOs. Appended block (the wave contract).
export type {
  BidLotRow,
  BidOptimizeResult,
  BidOptimizeSummary,
  EmailBidRequest,
} from './services/erp';
// P3.2 (track D) — Portal quote-request DTOs. Appended block (the wave contract).
export type { QuoteRequestConvertReceipt } from './services/erp';
// Track E (ROMS+Monday P4.7 staff position tracking) — appended block. (`ReportRunResult` already
// exports from the Reports block above; `ReportRunColumn` is its compatibility alias.)
export type { PositionTrackingState } from './position-tracking';
export {
  positionTracking$,
  startPositionTracking,
  stopPositionTracking,
} from './position-tracking';
export type { MyPositionReceipt, ReportRunColumn } from './services/erp';
// Ask PIPE (2026-08-16) — the `POST /api/ask` wire DTOs + the in-stream error. Appended block.
export type {
  AskAnswer,
  AskCitation,
  AskHistoryTurn,
  AskRequest,
  AskStatus,
} from './services/erp';
export { AskError } from './services/erp';

import { type Framework } from '@toeverything/infra';

import { ErpService } from './services/erp';

export function configureErpModule(framework: Framework) {
  // Root scope (NOT WorkspaceScope): the service holds session caches (entity schemas,
  // provisioning, hop lists) that are per-USER-session, not per-workspace — a workspace-scoped
  // instance was disposed on every facility-workspace switch, refetching everything. The service
  // has zero workspace-scoped deps, useService(ErpService) resolves up the scope chain (zero
  // call-site changes), and OIDC logout is a full page navigation, so nothing outlives a user.
  framework.service(ErpService);
}
// Build a Pallet wire types (checked against ops/pallets/__fixtures__/wire.json).
export type {
  AddPalletWeight,
  PalletAction,
  PalletItem,
  PalletKind,
  PalletMaterial,
  PalletMaterialPage,
  PalletPage,
  PalletRow,
  PalletSeries,
  PalletState,
  PalletView,
  PalletWeightLine,
} from './pallets-types';
export {
  PALLET_ACTIONS,
  PALLET_KINDS,
  PALLET_SERIES,
  PALLET_STATES,
} from './pallets-types';
// Eval sort station wire types (receiving→eval wave, slice D).
export type {
  BatchAction,
  BatchCheckResult,
  BatchCompare,
  BatchItem,
  BatchView,
  JobPriority,
  SortJobBatch,
  SortJobPage,
  SortJobRow,
  SortJobView,
} from './sort-types';
export {
  BATCH_ACTIONS,
  BATCH_CHECK_RESULTS,
  JOB_PRIORITIES,
} from './sort-types';
// Sales Orders wire types (checked against sales/orders/__fixtures__/wire.json).
export type {
  SalesAddLinesRequest,
  SalesBuyer,
  SalesChecklist,
  SalesChecklistRow,
  SalesLineItem,
  SalesLookup,
  SalesLookupGroup,
  SalesOrderAction,
  SalesOrderLine,
  SalesOrderPage,
  SalesOrderRow,
  SalesOrderState,
  SalesOrderTotals,
  SalesOrderView,
  SalesPalletBasis,
  SalesPiecesKind,
  SalesPriceBasis,
  SalesPriceResolution,
  SalesPriceSource,
  SalesStockFilters,
  SalesStockGroup,
  SalesStockGroups,
  SalesStockPage,
  SalesStockRow,
  SalesStockSheetBatch,
  SalesWeightSource,
} from './sales-orders-types';
export {
  SALES_ORDER_ACTIONS,
  SALES_ORDER_STATES,
  SALES_PALLET_BASES,
  SALES_PIECES_KINDS,
  SALES_PRICE_BASES,
  SALES_PRICE_SOURCES,
  SALES_WEIGHT_SOURCES,
} from './sales-orders-types';
